// Stationery "mini-Canva": fonts, decorative-aware templates, instantiation.
// Design space is 480 x 680 (portrait, ~A6). Every template renders from the SAME
// canvas JSON used by the editor, so thumbnails always match the editable design.

export const FONTS = [
  { id: "cormorant", label: "Cormorant", css: "'Cormorant Garamond', serif" },
  { id: "playfair", label: "Playfair Display", css: "'Playfair Display', serif" },
  { id: "garamond", label: "EB Garamond", css: "'EB Garamond', serif" },
  { id: "bodoni", label: "Bodoni Moda", css: "'Bodoni Moda', serif" },
  { id: "vibes", label: "Great Vibes (script)", css: "'Great Vibes', cursive" },
  { id: "montserrat", label: "Montserrat", css: "'Montserrat', sans-serif" },
  { id: "jakarta", label: "Jakarta Sans", css: "'Plus Jakarta Sans', sans-serif" },
];
export const fontCss = (id) => (FONTS.find((f) => f.id === id) || FONTS[0]).css;

export const CANVAS_W = 480;
export const CANVAS_H = 680;

export const uid = () =>
  (window.crypto?.randomUUID?.() || "id" + Math.random().toString(36).slice(2, 10));

const T = (o) => ({ type: "text", w: 400, x: 40, size: 22, font: "cormorant", color: "#222",
  weight: 400, italic: false, align: "center", spacing: 0, lineHeight: 1.15, rotation: 0, ...o });
const S = (o) => ({ type: "shape", shape: "line", fill: "#000", stroke: "#000", strokeWidth: 1, rotation: 0, ...o });
const D = (o) => ({ type: "decor", motif: "flourish", color: "#000", rotation: 0, ...o });
const I = (o) => ({ type: "image", src: "", fit: "cover", radius: 0, rotation: 0, ...o });

// A6 blush floral, black-tie, editorial, etc. Each has a distinct composition + palette + type.
export const TEMPLATES = [
  {
    id: "editorial", name: "Editorial Luxe", mood: "Épuré, magazine, contrasté",
    canvas: { w: 480, h: 680, bg: "#111110", layers: [
      T({ text: "SAVE THE DATE", y: 96, size: 15, spacing: 7, font: "montserrat", color: "#C9A227" }),
      T({ text: "{{names}}", y: 250, size: 58, font: "playfair", color: "#F4F0EA", lineHeight: 1.02 }),
      S({ x: 200, y: 372, w: 80, h: 1, fill: "#C9A227" }),
      T({ text: "{{date}}", y: 404, size: 22, font: "cormorant", color: "#E6E1D6" }),
      T({ text: "{{city}}", y: 446, size: 13, spacing: 5, font: "montserrat", color: "#9a948a" }),
      T({ text: "Réservez la date · plus de détails à suivre", y: 592, size: 14, italic: true, font: "cormorant", color: "#8f897f" }),
    ] } },
  {
    id: "blacktie", name: "Black Tie", mood: "Nocturne, doré, formel",
    canvas: { w: 480, h: 680, bg: "#0E0E10", layers: [
      S({ x: 60, y: 70, w: 360, h: 1, fill: "#C9A227" }),
      S({ x: 60, y: 78, w: 360, h: 1, fill: "#C9A227" }),
      T({ text: "TOGETHER WITH THEIR FAMILIES", y: 130, size: 12, spacing: 5, font: "montserrat", color: "#C9A227" }),
      T({ text: "{{names}}", y: 250, size: 52, font: "bodoni", color: "#FFFFFF", lineHeight: 1.05 }),
      T({ text: "vous invitent à célébrer leur mariage", y: 380, size: 17, italic: true, font: "cormorant", color: "#cfcabf" }),
      T({ text: "{{date}}", y: 440, size: 20, spacing: 3, font: "montserrat", color: "#e9e4d8" }),
      T({ text: "{{city}}", y: 476, size: 14, spacing: 3, font: "montserrat", color: "#a29c90" }),
      S({ x: 60, y: 600, w: 360, h: 1, fill: "#C9A227" }),
      S({ x: 60, y: 608, w: 360, h: 1, fill: "#C9A227" }),
    ] } },
  {
    id: "minimal", name: "Minimal Scandinave", mood: "Clair, aéré, moderne",
    canvas: { w: 480, h: 680, bg: "#FFFFFF", layers: [
      T({ text: "N O U S   N O U S   M A R I O N S", y: 120, size: 12, spacing: 2, font: "montserrat", color: "#9a938c" }),
      T({ text: "{{names}}", y: 280, size: 46, font: "montserrat", weight: 300, color: "#1C1B1A", lineHeight: 1.1 }),
      S({ x: 220, y: 380, w: 40, h: 1, fill: "#1C1B1A" }),
      T({ text: "{{date}}", y: 420, size: 16, spacing: 2, font: "montserrat", color: "#3b3835" }),
      T({ text: "{{city}}", y: 452, size: 13, spacing: 2, font: "montserrat", color: "#9a938c" }),
    ] } },
  {
    id: "modern", name: "Modern Éditorial", mood: "Net, contemporain, aligné à gauche",
    canvas: { w: 480, h: 680, bg: "#FFFFFF", layers: [
      S({ x: 48, y: 90, w: 60, h: 4, fill: "#3E7CB1" }),
      T({ text: "SAVE THE DATE", x: 48, w: 384, align: "left", y: 118, size: 13, spacing: 4, font: "montserrat", color: "#3E7CB1" }),
      T({ text: "{{names}}", x: 48, w: 384, align: "left", y: 250, size: 50, font: "playfair", color: "#111110", lineHeight: 1.02 }),
      T({ text: "{{date}}", x: 48, w: 384, align: "left", y: 420, size: 20, font: "montserrat", weight: 600, color: "#111110" }),
      T({ text: "{{city}}", x: 48, w: 384, align: "left", y: 452, size: 15, font: "montserrat", color: "#6b6660" }),
      S({ x: 48, y: 600, w: 384, h: 1, fill: "#e3e0da" }),
      T({ text: "Détails et RSVP sur notre site", x: 48, w: 384, align: "left", y: 616, size: 13, font: "montserrat", color: "#6b6660" }),
    ] } },
  {
    id: "monogram", name: "Classic Monogram", mood: "Raffiné, symétrique, doré",
    canvas: { w: 480, h: 680, bg: "#F3EFE9", layers: [
      D({ motif: "monogram", x: 140, y: 60, w: 200, h: 240, color: "#8A7A5C" }),
      T({ text: "{{names}}", y: 210, size: 40, font: "bodoni", color: "#2A2622", lineHeight: 1.05 }),
      T({ text: "SE MARIENT", y: 360, size: 12, spacing: 6, font: "montserrat", color: "#8A7A5C" }),
      T({ text: "{{date}}", y: 400, size: 20, font: "cormorant", color: "#4a453d" }),
      T({ text: "{{city}}", y: 436, size: 14, font: "cormorant", italic: true, color: "#7a746a" }),
      D({ motif: "leaf_divider", x: 165, y: 500, w: 150, h: 26, color: "#8A7A5C" }),
    ] } },
  {
    id: "champagne", name: "Champagne & Ivory", mood: "Doux, précieux, glamour",
    canvas: { w: 480, h: 680, bg: "#F7EFE3", layers: [
      D({ motif: "flourish", x: 130, y: 96, w: 220, h: 40, color: "#CBA35C" }),
      T({ text: "vous convient à leur mariage", y: 168, size: 16, italic: true, font: "cormorant", color: "#9a7f52" }),
      T({ text: "{{names}}", y: 250, size: 52, font: "playfair", color: "#4A3B2A", lineHeight: 1.02 }),
      T({ text: "{{date}}", y: 400, size: 22, font: "cormorant", color: "#5a4a34" }),
      T({ text: "{{city}}", y: 440, size: 14, spacing: 3, font: "montserrat", color: "#9a7f52" }),
      D({ motif: "flourish", x: 130, y: 560, w: 220, h: 40, color: "#CBA35C" }),
    ] } },
  {
    id: "floral", name: "Blush Floral", mood: "Romantique, aquarelle, tendre",
    canvas: { w: 480, h: 680, bg: "#FBF5F2", layers: [
      D({ motif: "floral_corner", x: -6, y: 470, w: 220, h: 220, color: "#C9899A" }),
      D({ motif: "floral_corner", x: 292, y: -6, w: 200, h: 200, color: "#C9899A", rotation: 180 }),
      T({ text: "Nous nous marions", y: 150, size: 20, italic: true, font: "cormorant", color: "#b06d80" }),
      T({ text: "{{names}}", y: 240, size: 50, font: "vibes", color: "#7a4a56", lineHeight: 1 }),
      D({ motif: "leaf_divider", x: 165, y: 360, w: 150, h: 26, color: "#C9899A" }),
      T({ text: "{{date}}", y: 400, size: 20, font: "cormorant", color: "#6a4650" }),
      T({ text: "{{city}}", y: 436, size: 14, font: "cormorant", italic: true, color: "#a3808a" }),
    ] } },
  {
    id: "romantic", name: "Romantique", mood: "Floral, doux, script",
    canvas: { w: 480, h: 680, bg: "#FAF1F1", layers: [
      T({ text: "SAVE THE DATE", y: 110, size: 12, spacing: 6, font: "montserrat", color: "#c68a98" }),
      T({ text: "{{names}}", y: 230, size: 56, font: "vibes", color: "#5A4650", lineHeight: 0.95 }),
      D({ motif: "rose_divider", x: 130, y: 350, w: 220, h: 60, color: "#D08C9A" }),
      T({ text: "{{date}}", y: 440, size: 22, font: "cormorant", color: "#5A4650" }),
      T({ text: "{{city}}", y: 478, size: 14, font: "cormorant", italic: true, color: "#8a707a" }),
    ] } },
  {
    id: "botanical", name: "Botanical Garden", mood: "Végétal, frais, naturel",
    canvas: { w: 480, h: 680, bg: "#EEF2E9", layers: [
      D({ motif: "leaf_branch", x: 110, y: 70, w: 260, h: 70, color: "#5f7358" }),
      T({ text: "Ensemble avec leurs familles", y: 190, size: 16, italic: true, font: "cormorant", color: "#5f7358" }),
      T({ text: "{{names}}", y: 270, size: 50, font: "playfair", color: "#33402B", lineHeight: 1.02 }),
      T({ text: "{{date}}", y: 420, size: 20, font: "cormorant", color: "#3d4a34" }),
      T({ text: "{{city}}", y: 456, size: 14, spacing: 2, font: "montserrat", color: "#6a7562" }),
      D({ motif: "sprig", x: 210, y: 540, w: 60, h: 110, color: "#5f7358" }),
    ] } },
  {
    id: "wildflowers", name: "Wildflowers", mood: "Champêtre, léger",
    canvas: { w: 480, h: 680, bg: "#FAF7F0", layers: [
      D({ motif: "sprig", x: 40, y: 60, w: 60, h: 130, color: "#9aa87f", rotation: -20 }),
      D({ motif: "sprig", x: 380, y: 60, w: 60, h: 130, color: "#9aa87f", rotation: 20 }),
      T({ text: "{{names}}", y: 260, size: 48, font: "cormorant", weight: 600, color: "#5A4A42", lineHeight: 1.05 }),
      D({ motif: "dots_divider", x: 160, y: 380, w: 160, h: 20, color: "#c1a06a" }),
      T({ text: "{{date}}", y: 420, size: 20, font: "cormorant", color: "#5A4A42" }),
      T({ text: "{{city}}", y: 456, size: 14, italic: true, font: "cormorant", color: "#8a7a6a" }),
    ] } },
  {
    id: "chateau", name: "Château Français", mood: "Classique, héraldique, doré",
    canvas: { w: 480, h: 680, bg: "#F6F1E7", layers: [
      D({ motif: "crest", x: 150, y: 56, w: 180, h: 210, color: "#B8860B" }),
      T({ text: "{{names}}", y: 300, size: 44, font: "bodoni", color: "#3A2E20", lineHeight: 1.05 }),
      D({ motif: "flourish", x: 140, y: 410, w: 200, h: 34, color: "#B8860B" }),
      T({ text: "célèbrent leur union", y: 456, size: 17, italic: true, font: "cormorant", color: "#6a5636" }),
      T({ text: "{{date}}", y: 512, size: 20, font: "cormorant", color: "#3A2E20" }),
      T({ text: "{{city}}", y: 548, size: 13, spacing: 3, font: "montserrat", color: "#8a7550" }),
    ] } },
  {
    id: "artdeco", name: "Art Déco Émeraude", mood: "Géométrique, luxe, vert & or",
    canvas: { w: 480, h: 680, bg: "#12271F", layers: [
      D({ motif: "deco_frame", x: 40, y: 40, w: 400, h: 600, color: "#C6A15B" }),
      T({ text: "SAVE THE DATE", y: 150, size: 13, spacing: 6, font: "montserrat", color: "#C6A15B" }),
      T({ text: "{{names}}", y: 270, size: 46, font: "bodoni", color: "#F3ECDC", lineHeight: 1.05 }),
      D({ motif: "dots_divider", x: 160, y: 400, w: 160, h: 20, color: "#C6A15B" }),
      T({ text: "{{date}}", y: 440, size: 20, font: "cormorant", color: "#EbE3D2" }),
      T({ text: "{{city}}", y: 476, size: 13, spacing: 3, font: "montserrat", color: "#b9a988" }),
    ] } },
  {
    id: "photo", name: "Photo Save the Date", mood: "Plein cadre, photo + texte",
    canvas: { w: 480, h: 680, bg: "#3a3a3a", layers: [
      I({ src: "", x: 0, y: 0, w: 480, h: 680, fit: "cover" }),
      { type: "shape", shape: "rect", x: 0, y: 400, w: 480, h: 280, fill: "rgba(10,10,12,0.55)", stroke: "none", strokeWidth: 0, rotation: 0 },
      T({ text: "SAVE THE DATE", y: 448, size: 13, spacing: 6, font: "montserrat", color: "#ffffff" }),
      T({ text: "{{names}}", y: 490, size: 46, font: "playfair", color: "#ffffff", lineHeight: 1.02 }),
      T({ text: "{{date}} · {{city}}", y: 600, size: 16, font: "cormorant", color: "#f0ece3" }),
    ] } },
];

export const TYPES = { save_the_date: "Save the Date", invitation: "Faire-part",
  details: "Carte d'informations", rsvp: "Carton RSVP", thankyou: "Remerciement", menu_card: "Carte menu" };

export function instantiate(tpl, { names, dateFr, city, siteUrl } = {}) {
  const map = { "{{names}}": names || "Vos prénoms", "{{date}}": dateFr || "Date à venir",
    "{{city}}": city || "Lieu à venir", "{{siteurl}}": siteUrl || "" };
  const layers = (tpl.canvas.layers || []).map((l) => {
    const n = { ...l, id: uid() };
    if (n.type === "text") n.text = String(n.text || "").replace(/\{\{names\}\}|\{\{date\}\}|\{\{city\}\}/g, (k) => map[k] ?? "");
    if (n.type === "qr") n.value = String(n.value || "").replace("{{siteurl}}", siteUrl || "");
    return n;
  });
  return { w: tpl.canvas.w, h: tpl.canvas.h, bg: tpl.canvas.bg, layers };
}

export const newTextLayer = (o = {}) => T({ id: uid(), text: "Nouveau texte", y: 320, ...o });
export const newDecorLayer = (motif) => D({ id: uid(), motif, x: 180, y: 300, w: 120, h: 120, color: "#8A7A5C" });
export const newShapeLayer = (shape) => S({ id: uid(), shape, x: 180, y: 320, w: shape === "line" ? 160 : 120, h: shape === "line" ? 2 : 120, fill: "#333", stroke: "#333", strokeWidth: shape === "line" ? 2 : 1 });
export const newQrLayer = (value) => ({ id: uid(), type: "qr", value: value || "", x: 190, y: 480, size: 110, color: "#111", bg: "#ffffff", rotation: 0 });
export const newImageLayer = (src) => I({ id: uid(), src, x: 160, y: 260, w: 160, h: 160, radius: 8 });
